import { PageShell, PolicySection } from "@/components/PageShell";

export default function PrivacyPolicy() {
  return (
    <PageShell
      title="Privacy Policy"
      testid="privacy-policy-page"
      subtitle="How Findivine collects, uses and protects your personal information. Last updated: July 2026."
    >
      <PolicySection title="1. Introduction">
        <p>
          This Privacy Policy applies to the website of <strong>Findivine</strong> (One Kind Earth Foundation),
          an NGO accepting donations for the welfare of society, and to the online donation and contact facilities
          offered through it. By using this website or making a donation, you agree to the practices described here.
        </p>
        <p>
          We handle personal data in accordance with applicable Indian law, including the Digital Personal Data
          Protection Act, 2023 and its rules, to the extent its provisions are in force and applicable to us.
        </p>
      </PolicySection>

      <PolicySection title="2. Information we collect">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Donor information</strong> — details you provide while donating, such as your name, email address,
            phone number, the cause you choose, and your donation amount and frequency.
          </li>
          <li>
            <strong>Transaction information</strong> — donation date, amount, payment method, and transaction or
            order references generated during processing.
          </li>
          <li>
            <strong>Contact form information</strong> — your name, email, phone number, subject and message when you
            write to us.
          </li>
          <li>
            <strong>Technical information</strong> — basic technical data such as browser type and pages visited,
            and cookies where applicable, used to keep the website functioning and improve it.
          </li>
        </ul>
      </PolicySection>

      <PolicySection title="3. How we use your information">
        <ul className="list-disc pl-6 space-y-2">
          <li>Processing and recording your donations.</li>
          <li>Issuing donation acknowledgements and receipts by email, where applicable.</li>
          <li>Responding to your enquiries, including payment issues, receipts and refund requests.</li>
          <li>Communicating with you about your donation and our activities, where appropriate.</li>
          <li>Maintaining accounting and transaction records.</li>
          <li>Meeting applicable legal, regulatory and reporting obligations.</li>
        </ul>
      </PolicySection>

      <PolicySection title="4. Payment processing">
        <p>
          Online donations are processed through third-party payment gateway providers (such as Razorpay, once
          enabled) and through UPI. Your card, bank account, UPI PIN and other sensitive payment credentials are
          entered on the payment provider's secure platform and are processed by them under their own policies and
          security standards.
        </p>
        <p>
          Findivine receives and retains the information needed to record and acknowledge your donation — such as
          your name, contact details, amount, payment method and transaction reference. We do not receive or store
          your full card number, CVV, net-banking password or UPI PIN.
        </p>
      </PolicySection>

      <PolicySection title="5. Sharing and disclosure">
        <p>
          We share personal information only with service providers who help us operate — such as payment gateway
          providers, email delivery services and hosting providers — and only to the extent needed for those
          services. We may also disclose information when required by law, regulation, or a lawful request from
          authorities. We do not sell donor information.
        </p>
      </PolicySection>

      <PolicySection title="6. Data retention and deletion">
        <p>
          We retain donation and transaction records for as long as needed for accounting, audit, legal and
          regulatory purposes. Other personal information is kept only for as long as it serves the purposes above.
          You may request deletion of your personal information, subject to records we are required to retain by law.
        </p>
      </PolicySection>

      <PolicySection title="7. Security">
        <p>
          We apply reasonable administrative and technical safeguards appropriate to the nature of the information
          we hold, including restricted access to administrative systems. No method of transmission or storage is
          completely secure, and we encourage donors to report any suspected misuse to us immediately.
        </p>
      </PolicySection>

      <PolicySection title="8. Your rights">
        <p>
          You may request access to, correction of, or erasure of your personal information, and you may raise a
          grievance about how your data is handled. To exercise these rights, contact us at{" "}
          <a href="mailto:info@findivine.org" className="text-primary underline underline-offset-2">info@findivine.org</a>.
          We will review and respond to genuine requests in accordance with applicable law.
        </p>
      </PolicySection>

      <PolicySection title="9. Cookies and analytics">
        <p>
          This website may use essential cookies and similar technologies required for core functionality (for
          example, remembering your language preference). If analytics or additional cookies are introduced, this
          policy will be updated to describe them.
        </p>
      </PolicySection>

      <PolicySection title="10. Updates to this policy">
        <p>
          We may update this Privacy Policy from time to time. The current version will always be published on this
          page with its effective date.
        </p>
      </PolicySection>

      <PolicySection title="11. Privacy contact">
        <p>
          For any privacy-related question or request, contact:{" "}
          <a href="mailto:info@findivine.org" data-testid="privacy-email" className="text-primary underline underline-offset-2">info@findivine.org</a>
        </p>
      </PolicySection>
    </PageShell>
  );
}
